/**
 * Shingare Global Presence — the single source of truth for the interactive world map
 * (homepage section + Company → Global Presence page).
 *
 * Source: Shingare's own export map (public/images/company/export-map.webp), read country by
 * country. It marks exactly the 49 locations below. Nothing here is inferred from geography:
 * a neighbouring country is NOT listed just because it is nearby.
 *
 * To change what the map shows, edit this list only — highlights, markers, region filters, region
 * counts and search all derive from it. No coordinates are needed: the marker is placed on the
 * country's own land by the map geometry, keyed by `isoCode`.
 *
 * Entry fields
 *   country       Display name.
 *   code          ISO 3166-1 alpha-2.
 *   isoCode       ISO 3166-1 alpha-3 (join key to src/data/worldMapGeometry.js).
 *   region        Filter/grouping bucket — one of REGIONS below.
 *   continent     Geographic continent (a region like "Middle East" is not a continent).
 *   active        false = keep the record but stop highlighting it.
 *   showOnMap     false = keep counting it but do not draw a marker.
 *   description   Optional verified copy shown in the country card. null = none is shown.
 *   link          Optional site route of a country/market page. null = no "Explore" link (none exist yet).
 *   isHome        Shingare's base — the origin of the connection arc (verified: site address is Nashik, India).
 *   sourceLabel   Only when the reference map's label differs from `country`.
 *   verify        Free-text note for the client; surfaces nowhere in the UI.
 */

/** Region buckets, in display order. `id` is the key stored on each country. */
export const REGIONS = [
  { id: "north-america", label: "North America" },
  { id: "south-america", label: "South America" },
  { id: "europe", label: "Europe" },
  { id: "middle-east", label: "Middle East" },
  { id: "africa", label: "Africa" },
  { id: "asia", label: "Asia" },
  { id: "oceania", label: "Oceania" },
];

const c = (country, code, isoCode, region, continent, extra = {}) => ({
  country, code, isoCode, region, continent,
  active: true, showOnMap: true, description: null, link: null,
  ...extra,
});

export const globalPresence = [
  // North America
  c("Canada", "CA", "CAN", "north-america", "North America"),
  c("United States", "US", "USA", "north-america", "North America"),
  c("Nicaragua", "NI", "NIC", "north-america", "North America"),

  // South America
  c("Colombia", "CO", "COL", "south-america", "South America"),
  c("Ecuador", "EC", "ECU", "south-america", "South America"),
  c("Venezuela", "VE", "VEN", "south-america", "South America"),
  c("Brazil", "BR", "BRA", "south-america", "South America"),
  c("Chile", "CL", "CHL", "south-america", "South America"),

  // Europe
  c("United Kingdom", "GB", "GBR", "europe", "Europe"),
  c("France", "FR", "FRA", "europe", "Europe"),
  c("Spain", "ES", "ESP", "europe", "Europe"),
  c("Italy", "IT", "ITA", "europe", "Europe"),
  c("Belarus", "BY", "BLR", "europe", "Europe"),
  c("Ukraine", "UA", "UKR", "europe", "Europe"),
  c("Russia", "RU", "RUS", "europe", "Europe", { verify: "Transcontinental; grouped under Europe. Move to Asia if the client prefers." }),

  // Middle East
  c("Turkey", "TR", "TUR", "middle-east", "Asia"),
  c("Iraq", "IQ", "IRQ", "middle-east", "Asia"),
  c("Iran", "IR", "IRN", "middle-east", "Asia"),
  c("Saudi Arabia", "SA", "SAU", "middle-east", "Asia"),
  c("Oman", "OM", "OMN", "middle-east", "Asia"),

  // Africa
  c("Morocco", "MA", "MAR", "africa", "Africa"),
  c("Egypt", "EG", "EGY", "africa", "Africa", { verify: "Geographically Africa (often also counted as Middle East). Grouped under Africa." }),
  c("Sudan", "SD", "SDN", "africa", "Africa"),
  c("Ethiopia", "ET", "ETH", "africa", "Africa"),
  c("Uganda", "UG", "UGA", "africa", "Africa"),
  c("Kenya", "KE", "KEN", "africa", "Africa"),
  c("Tanzania", "TZ", "TZA", "africa", "Africa"),
  c("Zimbabwe", "ZW", "ZWE", "africa", "Africa"),
  c("South Africa", "ZA", "ZAF", "africa", "Africa"),

  // Asia
  c("Kazakhstan", "KZ", "KAZ", "asia", "Asia"),
  c("Turkmenistan", "TM", "TKM", "asia", "Asia"),
  c("Afghanistan", "AF", "AFG", "asia", "Asia"),
  c("India", "IN", "IND", "asia", "Asia", { isHome: true, description: "Shingare Industries is based in Nashik, India." }),
  c("Bangladesh", "BD", "BGD", "asia", "Asia"),
  c("Myanmar", "MM", "MMR", "asia", "Asia"),
  c("Thailand", "TH", "THA", "asia", "Asia"),
  c("Sri Lanka", "LK", "LKA", "asia", "Asia"),
  c("Malaysia", "MY", "MYS", "asia", "Asia"),
  c("Singapore", "SG", "SGP", "asia", "Asia"),
  c("Indonesia", "ID", "IDN", "asia", "Asia"),
  c("Vietnam", "VN", "VNM", "asia", "Asia"),
  c("Philippines", "PH", "PHL", "asia", "Asia"),
  c("China", "CN", "CHN", "asia", "Asia"),
  c("Taiwan", "TW", "TWN", "asia", "Asia"),
  c("Hong Kong", "HK", "HKG", "asia", "Asia", { verify: "Special Administrative Region of China, marked separately on the reference map." }),

  // Oceania
  c("Papua New Guinea", "PG", "PNG", "oceania", "Oceania", {
    sourceLabel: "Guinea",
    verify: "The reference map labels this marker just \"Guinea\", but the marker sits on the island of New Guinea (Papua New Guinea). Guinea in West Africa carries no marker. Please confirm with the client.",
  }),
  c("Fiji", "FJ", "FJI", "oceania", "Oceania", { sourceLabel: "Fizi" }),
  c("Australia", "AU", "AUS", "oceania", "Oceania"),
  c("New Zealand", "NZ", "NZL", "oceania", "Oceania"),
];

// ---- derived helpers (everything below computes from the list above; never hard-code counts) ----

/** Countries that should appear on the map. */
export const visiblePresence = () => globalPresence.filter((p) => p.active && p.showOnMap);

const byIso = new Map(globalPresence.map((p) => [p.isoCode, p]));
export const presenceByIso = (isoCode) => byIso.get(isoCode) || null;

export const regionLabel = (id) => REGIONS.find((r) => r.id === id)?.label || id;

/** Only regions that actually have at least one active country, each with its computed count and countries. */
export function regionSummaries(list = globalPresence) {
  const active = list.filter((p) => p.active);
  return REGIONS.map((r) => {
    const countries = active.filter((p) => p.region === r.id);
    return { ...r, count: countries.length, countries };
  }).filter((r) => r.count > 0);
}

/** Number of distinct continents represented by the active countries. */
export const continentCount = (list = globalPresence) => new Set(list.filter((p) => p.active).map((p) => p.continent)).size;
